/**
 * 회사 용어집 — 외부 API 탭의 여섯째 보기 (2026-10-23 · 마이그 240 · 시안 G 2판 ＋ 평 PG · PG2).
 *
 * 사용자 답: 「안 A — 메뉴 칸 · 표」 · 「용어 몫을 따로 고정」 · 「넣을 때 고르기」(기본값 없음) · 「거기서 멈춤 — 경계 하나」.
 *
 * ```
 * 띠     받아 적기 힌트에 들어가는 용어 N개 / 전체 M개 · 용어 몫 160자 중 K자 — 회의마다 같다(몫을 따로 둔다)
 * 표     차례(▲▼ · 맨 위 줄의 ▲ · 맨 아래 줄의 ▼ 는 잠김) · 용어 · 메모(관리자용) · 힌트(들어감 · 한도 밖 · 꺼짐) · 켜기 · 넣은 사람 · ⋯
 * 경계   마지막 «들어감» 줄 아래에 한 줄 — 「여기까지 힌트에 들어갑니다」(찾는 중에는 안 그림 · 차례가 끊겨 보이므로)
 * ⋯     고치기 · 맨 위로 · 지우기(지우면 아래 띠에 «되돌리기»)
 * ```
 * ★ 상태는 서버가 받아 적기와 «같은 함수»로 정해 준다 — 콘솔이 다시 세지 않는다.
 * ⛔ 끌어 옮기기는 두지 않는다 — ▲▼ 와 「맨 위로」만(끌기를 그려 놓고 안 되면 「눌러도 아무 일이 없는」 병이다).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Check, ChevronDown, ChevronUp, MoreHorizontal } from '../../../icons';
import {
  deleteGlossaryTerm, getGlossary, listGlossaryInstitutions, moveGlossaryTerm, patchGlossaryTerm, restoreGlossaryTerm,
  type GlossaryDeleted, type GlossaryInstitution, type GlossaryMove, type GlossaryTerm, type GlossaryView,
} from '../../../api/voiceGlossary';
import { errText } from './ExternalApiEditModal';
import { GlossaryBulkModal, GlossaryTermModal } from './GlossaryModals';
import { meterPercent, shortDay } from './glossaryView';
import './Glossary.css';

/** 되돌리기 띠가 머무는 시간. */
export const GLOSSARY_UNDO_MS = 8000;

const STATE_BADGE: Record<GlossaryTerm['state'], { cls: string; label: string }> = {
  in: { cls: 'xa-badge--ok', label: '들어감' },
  out: { cls: 'xa-badge--warn', label: '한도 밖' },
  off: { cls: 'xa-badge--muted', label: '꺼짐' },
};

export interface GlossaryPanelProps {
  q: string;
  /** 툴바의 「용어 추가」 · 「여러 개 넣기」 — 눌린 것을 받아 팝업을 열고 `onAskDone` 으로 돌려준다. */
  ask: 'add' | 'bulk' | null;
  onAskDone: () => void;
  onCount?: (n: { total: number; inHint: number }) => void;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'not-served' }
  | { kind: 'no-table' }
  | { kind: 'error'; message: string }
  | { kind: 'ready' };

export function GlossaryPanel({ q, ask, onAskDone, onCount }: GlossaryPanelProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [insts, setInsts] = useState<GlossaryInstitution[]>([]);
  const [inst, setInst] = useState<string | null>(null);
  const [view, setView] = useState<GlossaryView | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<GlossaryTerm | null>(null);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [undo, setUndo] = useState<GlossaryDeleted | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    listGlossaryInstitutions()
      .then((r) => {
        if (!alive) return;
        if (!r.ready) { setLoad({ kind: 'not-served' }); return; }
        if (!r.available) { setLoad({ kind: 'no-table' }); return; }
        setInsts(r.institutions);
        setInst((cur) => cur ?? r.institutions[0]?.institution_id ?? null);
        setLoad({ kind: 'ready' });
      })
      .catch((e: unknown) => { if (alive) setLoad({ kind: 'error', message: errText(e, '용어집을 불러오지 못했습니다') }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (inst === null) { setView(null); return undefined; }
    let alive = true;
    setView(null); setError(null);
    getGlossary(inst)
      .then((v) => { if (alive) setView(v); })
      .catch((e: unknown) => { if (alive) setError(errText(e, '용어집을 불러오지 못했습니다')); });
    return () => { alive = false; };
  }, [inst]);

  useEffect(() => { onCount?.({ total: view?.total ?? 0, inHint: view?.in_hint ?? 0 }); }, [view, onCount]);
  useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current); }, []);

  const ready = load.kind === 'ready' && inst !== null && view !== null && view.available;
  useEffect(() => {
    if (ask === null) return;
    if (ready) { if (ask === 'add') setAdding(true); else setBulk(true); }
    onAskDone();
  }, [ask, ready, onAskDone]);

  /** 바뀐 뒤의 표 — 기관 목록의 수도 함께 고친다. */
  const apply = useCallback((v: GlossaryView) => {
    setView(v);
    setInsts((xs) => xs.map((x) => (x.institution_id === inst ? { ...x, term_count: v.total } : x)));
  }, [inst]);

  async function run(id: string, fn: () => Promise<GlossaryView>, fail: string) {
    setBusyId(id); setError(null); setMenuFor(null);
    try { apply(await fn()); } catch (e: unknown) { setError(errText(e, fail)); } finally { setBusyId(null); }
  }
  const move = (t: GlossaryTerm, to: GlossaryMove) => void run(t.id, () => moveGlossaryTerm(inst!, t.id, to), '차례를 바꾸지 못했습니다');
  const toggle = (t: GlossaryTerm, on: boolean) => void run(t.id, () => patchGlossaryTerm(inst!, t.id, { enabled: on }), '켜기를 바꾸지 못했습니다');

  async function remove(t: GlossaryTerm) {
    setBusyId(t.id); setError(null); setMenuFor(null); setNotice(null);
    try {
      const r = await deleteGlossaryTerm(inst!, t.id);
      apply(r);
      setUndo(r.deleted);
      if (undoTimer.current) clearTimeout(undoTimer.current);
      undoTimer.current = setTimeout(() => setUndo(null), GLOSSARY_UNDO_MS);
    } catch (e: unknown) {
      setError(errText(e, '지우지 못했습니다'));
    } finally { setBusyId(null); }
  }

  async function restore() {
    if (!undo || !inst) return;
    const d = undo;
    setUndo(null);
    if (undoTimer.current) clearTimeout(undoTimer.current);
    try {
      apply(await restoreGlossaryTerm(inst, d));
      setNotice(`「${d.term}」 용어를 ${d.position}번 자리에 되살렸습니다.`);
    } catch (e: unknown) {
      setError(errText(e, '되살리지 못했습니다'));
    }
  }

  const rows = useMemo(() => {
    const all = view?.terms ?? [];
    const needle = q.trim().toLowerCase();
    return needle ? all.filter((t) => t.term.toLowerCase().includes(needle) || (t.memo ?? '').toLowerCase().includes(needle)) : all;
  }, [view, q]);
  const searching = q.trim() !== '';
  const lastIn = useMemo(() => {
    const all = view?.terms ?? [];
    let at = -1;
    all.forEach((t, i) => { if (t.state === 'in') at = i; });
    return at;
  }, [view]);
  const hasOut = (view?.terms ?? []).some((t) => t.state === 'out');

  if (load.kind === 'loading') return <div className="gl"><p className="xa-dim gl-pad">불러오는 중…</p></div>;
  if (load.kind === 'not-served') {
    return (
      <div className="gl">
        <div className="xa-band xa-band--info" role="status">
          <BookOpen size={18} aria-hidden />
          <span className="xa-band__t"><b>용어집 서버가 아직 올라오지 않았습니다</b><span>chat-server 를 이 판으로 배포하면 저절로 열립니다.</span></span>
        </div>
      </div>
    );
  }
  if (load.kind === 'no-table') {
    return (
      <div className="gl">
        <div className="xa-band xa-band--info" role="status">
          <BookOpen size={18} aria-hidden />
          <span className="xa-band__t"><b>용어집 표가 아직 없습니다</b><span>마이그 240 을 적용하면 저절로 열립니다. 그동안 받아 적기는 회의 제목 · 참석자만 힌트로 씁니다.</span></span>
        </div>
      </div>
    );
  }
  if (load.kind === 'error') {
    return <div className="gl"><div className="xa-band xa-band--bad" role="alert"><AlertTriangle size={18} aria-hidden /><span className="xa-band__t"><b>{load.message}</b></span></div></div>;
  }

  return (
    <div className="gl">
      <div className="gl-head">
        <label className="gl-inst">
          <span>기관</span>
          <select className="xa-select" value={inst ?? ''} onChange={(e) => { setInst(e.target.value); setUndo(null); setNotice(null); }}>
            {insts.map((i) => (
              <option key={i.institution_id} value={i.institution_id}>{i.name}{i.code ? ` (${i.code})` : ''} · {i.term_count}개</option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="xa-band xa-band--bad" role="alert"><AlertTriangle size={18} aria-hidden /><span className="xa-band__t"><b>{error}</b></span></div>}
      {notice && <div className="xa-band xa-band--ok" role="status"><Check size={18} aria-hidden /><span className="xa-band__t"><b>{notice}</b></span></div>}

      {view === null ? <p className="xa-dim gl-pad">불러오는 중…</p> : view.total === 0 ? (
        <>
          <div className="xa-band xa-band--info" role="status">
            <BookOpen size={18} aria-hidden />
            <span className="xa-band__t"><b>등록된 용어가 없습니다</b><span>없으면 받아 적기는 회의 제목 · 참석자만 힌트로 씁니다.</span></span>
          </div>
          <div className="gl-empty">
            <p>회사에서 자주 틀리게 받아 적히는 이름 · 제품 · 약어를 넣어 보세요.</p>
            <span className="gl-empty__acts">
              <button type="button" className="xa-btn" onClick={() => setBulk(true)}>여러 개 넣기</button>
              <button type="button" className="xa-btn xa-btn--primary" onClick={() => setAdding(true)}>용어 추가</button>
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="xa-band xa-band--info gl-meter" role="status">
            <BookOpen size={18} aria-hidden />
            <span className="xa-band__t">
              <b>받아 적기 힌트에 들어가는 용어 {view.in_hint}개 / 전체 {view.total}개</b>
              <span className="gl-meter__bar" aria-hidden><i style={{ width: `${meterPercent(view.used_chars, view.budget_chars)}%` }} /></span>
              <span>
                용어 몫 {view.budget_chars}자 중 {view.used_chars}자 · 회의마다 같습니다 — 회의 제목 · 참석자와 따로 둡니다.
                위에서부터 들어가니 자주 틀리는 말을 위로 올리세요.
              </span>
            </span>
          </div>

          <table className="cl-table gl-table">
            <thead>
              <tr>
                <th className="gl-ord">차례</th>
                <th>용어</th>
                <th>메모 <span className="gl-sub">(관리자용 · 힌트에 안 들어감)</span></th>
                <th>힌트</th>
                <th>켜기</th>
                <th>넣은 사람</th>
                <th className="cl-table__act"><span className="gl-sr">더보기</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="xa-dim">찾는 용어가 없습니다</td></tr>
              )}
              {rows.map((t) => {
                const first = t.position === 1;
                const last = t.position === view.total;
                const busy = busyId === t.id;
                const badge = STATE_BADGE[t.state];
                return (
                  <Row
                    key={t.id}
                    t={t}
                    cutAfter={!searching && hasOut && view.terms[lastIn]?.id === t.id}
                    first={first}
                    last={last}
                    busy={busy}
                    badge={badge}
                    menuOpen={menuFor === t.id}
                    onMenu={(open) => setMenuFor(open ? t.id : null)}
                    onMove={(to) => move(t, to)}
                    onToggle={(on) => toggle(t, on)}
                    onEdit={() => { setMenuFor(null); setEditing(t); }}
                    onDelete={() => void remove(t)}
                  />
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {undo && (
        <div className="gl-toast" role="status">
          <span>「{undo.term}」 용어를 지웠습니다</span>
          <button type="button" className="gl-toast__btn" onClick={() => void restore()}>되돌리기</button>
        </div>
      )}

      {view && inst && (adding || editing) && (
        <GlossaryTermModal
          inst={inst}
          term={editing}
          termMax={view.term_max}
          memoMax={view.memo_max}
          maxTerms={view.max_terms}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={(v, msg) => { setAdding(false); setEditing(null); apply(v); setNotice(msg); }}
        />
      )}
      {view && inst && bulk && (
        <GlossaryBulkModal
          inst={inst}
          termMax={view.term_max}
          maxTerms={view.max_terms}
          onClose={() => setBulk(false)}
          onSaved={(v, msg) => { setBulk(false); apply(v); setNotice(msg); }}
        />
      )}
    </div>
  );
}

interface RowProps {
  t: GlossaryTerm;
  cutAfter: boolean;
  first: boolean;
  last: boolean;
  busy: boolean;
  badge: { cls: string; label: string };
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  onMove: (to: GlossaryMove) => void;
  onToggle: (on: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}

function Row({ t, cutAfter, first, last, busy, badge, menuOpen, onMenu, onMove, onToggle, onEdit, onDelete }: RowProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) onMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onMenu(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen, onMenu]);

  return (
    <>
      <tr className={busy ? 'is-busy' : undefined}>
        <td className="gl-ord">
          <span className="gl-ord__in">
            <button type="button" className="cl-rowbtn" aria-label={`「${t.term}」 위로`} disabled={first || busy} onClick={() => onMove('up')}><ChevronUp size={14} /></button>
            <button type="button" className="cl-rowbtn" aria-label={`「${t.term}」 아래로`} disabled={last || busy} onClick={() => onMove('down')}><ChevronDown size={14} /></button>
            <span className="gl-ord__n">{t.position}</span>
          </span>
        </td>
        <td><b className="gl-term">{t.term}</b></td>
        <td className="gl-memo" title={t.memo ?? undefined}>{t.memo ?? <span className="xa-dim">—</span>}</td>
        <td>
          <span className={`xa-badge ${badge.cls}`}>
            {t.state === 'in' && <Check size={12} aria-hidden />}
            {badge.label}
          </span>
        </td>
        <td>
          <label className="gl-switch" title={t.enabled ? '켜짐' : '꺼짐'}>
            <input type="checkbox" role="switch" checked={t.enabled} disabled={busy} aria-label={`「${t.term}」 켜기`} onChange={(e) => onToggle(e.target.checked)} />
            <span className="gl-switch__track" />
            <span className="gl-switch__thumb" />
          </label>
        </td>
        <td className="gl-who">
          {t.created_by_name ?? <span className="xa-dim">—</span>}
          <span className="xa-dim"> · {shortDay(t.created_at)}</span>
        </td>
        <td className="cl-table__act">
          <div className="gl-more" ref={menuRef}>
            <button type="button" className="cl-rowbtn" aria-label={`「${t.term}」 더보기`} aria-haspopup="menu" aria-expanded={menuOpen} disabled={busy} onClick={() => onMenu(!menuOpen)}>
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div className="gl-menu" role="menu">
                <button type="button" role="menuitem" onClick={onEdit}>고치기</button>
                <button type="button" role="menuitem" disabled={first} onClick={() => onMove('top')}>맨 위로</button>
                <button type="button" role="menuitem" className="is-danger" onClick={onDelete}>지우기</button>
              </div>
            )}
          </div>
        </td>
      </tr>
      {cutAfter && (
        <tr className="gl-cut">
          <td colSpan={7}>여기까지 힌트에 들어갑니다 · 아래의 켠 용어는 «한도 밖»입니다</td>
        </tr>
      )}
    </>
  );
}
